import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '@tcc/crate';
import { useLoggedInUser, useFeatureFlags } from '@tcc/shared-data-lib';
import { useHistory, useLocation } from 'react-router-dom';
import lift from '@tcc/load-lifter';
import { getMessage } from '@tcc/i18n';
import { importFromS3 } from '../../utils/importFromS3';
import { ROUTE } from '../../constants/routes';
import { useUserLoggedInConfigs, useFeatureFlagsConfigs } from '../../utils/sharedData';
import { useNavigationLinks } from '../../hooks/useNavigationLinks/useNavigationLinks';
import { Operation } from '../../hooks/useOperations/types';
import { shouldShowOperationSelector } from '../../utils/shouldShowOperationSelector';
import { shouldShowMultiOperationSelector } from '../../utils/shouldShowMultiOperationSelector';
import { toggleOperationSelectionMode } from '../../utils/toggleOperationSelectionMode';
import { getOperationSelectorDisplayText } from '../../utils/getOperationSelectorDisplayText';
import { getSupportUrl } from '../../utils/support';
import { HermesLibrary } from '../../types/Hermes';

type UserInfoProps = {
  email: string;
  fullName: string;
  avatar: string;
};

type SubMenuItemProps = {
  label: string;
  glyph: string;
  dataTestId?: string;
  onClick: () => void;
};

type NavLink = {
  glyph?: string;
  icon?: React.ReactElement;
  label: string;
  href?: string;
  testId?: string;
  target?: string;
  isActive?: boolean;
};

type NavMenu = {
  id: string,
  links: NavLink[];
};

type Props = {
  allOperationsDisplayMessage: string
  areNotificationsLoading: boolean;
  expanded: boolean;
  hasMultiOperationEnabled: boolean;
  hasOperationDropdown: boolean;
  menuLinks: NavLink[];
  menues: NavMenu[];
  onLogoClick?: () => void;
  operations: Operation[];
  operationsSelected: Operation[];
  operationsSelectedDisplayMessage: string;
  setExpandedState: (isExpanded: boolean) => void;
  setSelectedOperations: (operationSelected: Operation[]) => void;
  subMenusUserProfile: SubMenuItemProps[];
  support?: NavLink;
  expandCollapseButton: NavLink;
  userInfo: UserInfoProps;
};

interface IProps {
  expanded: boolean;
  operations: Operation[];
  operationsSelected: Operation[];
  setExpandedState: (isExpanded: boolean) => void;
  setSelectedOperations: (operationSelected: Operation[]) => void;
}

const InformationArchitecturePlugin: React.FC<Props> = React.lazy(() =>
  importFromS3('information-architecture')
);

export const InfoArchitecturePluginContainer: React.FC<IProps> = ({
  expanded,
  operations,
  operationsSelected,
  setExpandedState,
  setSelectedOperations,
}: IProps) => {
  const { links, menues } = useNavigationLinks();
  const { user, user: { details } } = useLoggedInUser(useUserLoggedInConfigs);
  const { featureFlags } = useFeatureFlags(useFeatureFlagsConfigs(user));
  const [areNotificationsLoading, setNotificationsLoading] = useState(true);
  const history = useHistory();
  const location = useLocation();


  useEffect(() => {
    if (user && operationsSelected.length > 0) {
      lift(['tcc-hermes'], (Hermes: HermesLibrary) => {
        setNotificationsLoading(false);
        Hermes.install(
          null,
          {
            containerSelector: '#hermesButton',
            insideDashboard: false,
          },
          user.details,
          operationsSelected[0].operationId
        );
      });
    }
  }, [user, operationsSelected]);

  const userInfo = {
    fullName: `${details?.firstName} ${details?.lastName}`,
    email: details?.email,
    avatar: `${details?.firstName?.charAt(0)}${details?.lastName?.charAt(0)}`,
  };

  const hasOperationDropdown = shouldShowOperationSelector({ user, route: location.pathname }) && operations.length !== 0;

  const hasMultiOperationEnabled = shouldShowMultiOperationSelector(location.pathname, featureFlags);

  toggleOperationSelectionMode({
    hasMultiOperationEnabled,
    operationsSelected,
    operations,
    setSelectedOperations
  });

  const operationsSelectedDisplayMessage = getOperationSelectorDisplayText({
    operations,
    operationsSelected
  });

  const subMenus = useMemo(() => [
    {
      label: getMessage('sidenav.manageAccount'),
      dataTestId: 'information-architecture-manage-account',
      glyph: Icon.set.COG,
      onClick: () => {
        const settingsPluginEnabled = featureFlags['web.settings-plugin-version'];

        if (settingsPluginEnabled) {
          history.push(`${ROUTE.Settings}${location.search}`);
        } else {
          window.location.assign('https://climate.com/static/settings/index.html');
        }
      },
    },
    {
      label: getMessage('sidenav.logOut'),
      dataTestId: 'information-architecture-logout',
      glyph: Icon.set.ARROW_RIGHT_CIRCLE,
      onClick: () => history.push(`${ROUTE.Auth}/logout`),
    },
  ], [history, featureFlags, location]);

  const support = useMemo(() => ({
    icon: <Icon glyph={Icon.set.IA_LIST} />,
    href: getSupportUrl(details?.countryCode),
    testId: 'information-architecture-support',
    target: '_blank',
    label: getMessage('sidenav.support')
  }), [details]);

  const expandCollapseButton = useMemo(() => ({
    icon: expanded ? <Icon glyph={Icon.set.IA_COLLAPSE} />: <Icon glyph={Icon.set.IA_EXPAND } />,
    testId: expanded ?  'information-architecture-collapse-button' : 'information-architecture-expand-button',
    onClick: () => setExpandedState(!expanded),
    label: expanded ? getMessage('sidenav.collapseMenu') : getMessage('sidenav.openMenu')
  }), [expanded, setExpandedState]);

  const onLogoClick = useCallback(() => {
    history.push(`/${location.search}`);
  }, [history, location]);

  return (
    <InformationArchitecturePlugin
      allOperationsDisplayMessage={getMessage('sidenav.allOperations')}
      areNotificationsLoading={areNotificationsLoading}
      expanded={expanded}
      hasOperationDropdown={hasOperationDropdown}
      hasMultiOperationEnabled={hasMultiOperationEnabled}
      menuLinks={links}
      menues={menues}
      onLogoClick={onLogoClick}
      operations={operations}
      operationsSelected={operationsSelected}
      operationsSelectedDisplayMessage={operationsSelectedDisplayMessage}
      setExpandedState={setExpandedState}
      setSelectedOperations={setSelectedOperations}
      subMenusUserProfile={subMenus}
      support={support}
      expandCollapseButton={expandCollapseButton}
      userInfo={userInfo}
    />
  );
};
